import { print } from 'graphql';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import createOneMessageMutation from '~/shared/graphql-queries/messages/create-one-message-mutation';
import markMessagesAsReadMutation from '~/shared/graphql-queries/messages/mark-messages-as-read-mutation';
import messageFragment from '~/shared/graphql-queries/messages/message-fragment';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationMessageResponse = Readonly<{
  data?: {
    paginationMessageQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type PaginationMessageResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type PaginationMessageArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
}>;

type CreateOneMessageArgs = Readonly<{
  text?: string;
  type?: string;
  data?: Record<string, unknown>;
  repliedMessage?: string;
  course?: string;
  isMetaMessage?: boolean;
}>;

const transformMessageResponse =
  (type: 'paginationMessageQuery') =>
  (response: PaginationMessageResponse): PaginationMessageResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const messagesTagTypes = ['messages'] as const;

export const getMessagesQuery = (builder: BuilderType) => {
  return {
    paginationMessage: builder.query<PaginationMessageResult, PaginationMessageArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'message',
              fragment: messageFragment,
              filterType: 'FilterFindManyMessageInput',
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? SORT_TYPE._ID_DESC,
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['messages'],
      transformResponse: transformMessageResponse('paginationMessageQuery'),
    }),

    createOneMessage: builder.mutation<unknown, CreateOneMessageArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneMessageMutation),
          variables: {
            text: args?.text,
            type: args?.type,
            data: args?.data,
            repliedMessage: args?.repliedMessage,
            course: args?.course,
            isMetaMessage: args?.isMetaMessage,
          },
        }),
      }),
    }),
    markMessagesAsRead: builder.mutation<unknown, CreateOneMessageArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(markMessagesAsReadMutation),
          variables: {
            dialog: args?.dialog,
            messageIds: args?.messageIds,
          },
        }),
      }),
    }),
  };
};
